import { useEffect, useState } from 'react';

import { Grid, InputAdornment, useMediaQuery, useTheme } from '@mui/material';

import TAGButton from '@/components/TAGButton';
import TAGTextFieldInput from '@/components/TAGTextFieldInput';
import { BffBrandType, BffFacility } from '@/types/generated';
import TAGSvgIcon from '@/components/TAGSvgIcon';
import { getBffFacilitiesByAddress } from '@/services/getBffFacilitiesByAddress';
import { useAppContext, useFacilityContext } from '@/context';
import { mapSearchClickEvent } from '@/utils';

import {
	locationSearchCtrStyles,
	locationSearchLocateMeBtnStyles,
	getLocationSearchTextInputStyles,
} from './LocationSearch.styles';

export interface ILocationSearch {
	updateDataFromLocationSearch: (facilitiesByAddress: BffFacility[]) => void;
	revertToCurrentFacility: () => void;
	onSearchQueryChange?: (searchQuery: string) => void;
	searchQueryValueFromUrl: string;
}

export default function LocationSearch(props: Readonly<ILocationSearch>) {
	const { updateDataFromLocationSearch, revertToCurrentFacility, searchQueryValueFromUrl = '' } = props;
	const theme = useTheme();
	const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

	const { facilityBrand } = useFacilityContext();
	const { environment, config, appName, appVersion } = useAppContext();
	const url =
		environment === 'prod' ? config.services.prod.BEFFE_GRAPHQL_URL : config.services.nonprod.BEFFE_GRAPHQL_URL;
	const fetchAllLocationsOnEmptyResults = config.featureFlags?.fetchAllLocationsOnEmptyResults;
	const placeholder = 'ZIP code, City or State';

	const inputProps = { 'data-test-id': 'locations_search_input_box', 'aria-label': placeholder };

	const [searchQuery, setSearchQuery] = useState('');

	useEffect(() => {
		if (searchQueryValueFromUrl) {
			setSearchQuery(searchQueryValueFromUrl);
		}
	}, [searchQueryValueFromUrl]);

	const fetchBffFacilityData = async () => {
		const queryString = searchQuery.trim();
		if (!queryString || queryString === '') {
			return clearAndRevertToSelectedFacility();
		}
		// First try searching by address
		const facilitiesList = await getBffFacilitiesByAddress(
			facilityBrand.toString() as BffBrandType,
			queryString,
			url,
			fetchAllLocationsOnEmptyResults,
			appName,
			appVersion
		);

		updateDataFromLocationSearch((facilitiesList?.facilitiesByAddress as BffFacility[]) || []);
	};

	const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSearchQuery(e.currentTarget.value);
		if (props.onSearchQueryChange) {
			props.onSearchQueryChange(e.currentTarget.value);
		}
	};

	const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			void mapSearchClickEvent();
			void fetchBffFacilityData();
		}
	};

	const clearAndRevertToSelectedFacility = () => {
		mapSearchClickEvent();
		setSearchQuery('');
		revertToCurrentFacility();
	};

	return (
		<Grid
			item
			container
			data-test-id="locations_search"
			display="flex"
			flexDirection="column"
			sx={locationSearchCtrStyles}
		>
			{/* locate me */}
			<Grid container data-test-id="locations_search_title_and_button" alignItems="center">
				<Grid item md={8} xs={10} sx={{ paddingRight: '1rem' }}>
					<TAGTextFieldInput
						fieldPlaceHolder={placeholder}
						onChange={handleSearchInputChange}
						onKeyDown={handleKeyPress}
						value={searchQuery}
						inputSx={getLocationSearchTextInputStyles(theme)}
						inputProps={inputProps}
						startAdornment={
							<InputAdornment position="start">
								<TAGSvgIcon data-test-id="locations_search_icon_search" icon="FocusTarget" size={20} />
							</InputAdornment>
						}
					/>
				</Grid>
				<Grid item md={4} xs={2}>
					<TAGButton
						size="S"
						variant="secondaryDefault"
						aria-label="Locate me"
						startIcon={<TAGSvgIcon icon="LVLocationArrow" size={20} />}
						onClick={clearAndRevertToSelectedFacility}
						sx={locationSearchLocateMeBtnStyles}
					>
						{!isSmallScreen && 'Locate me'}
					</TAGButton>
				</Grid>
			</Grid>
		</Grid>
	);
}
